import React, {useState} from "react";
import './FormInput.scss'
import TextField from '@mui/material/TextField';
import {InputTheme, SmallInputTheme} from "../../Pickers/InputTheme";
import {IconButton, InputAdornment} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {ThemeProvider} from "@mui/material/styles";
import {makeStyles, createStyles} from '@mui/styles';
import _ from 'lodash'

// Move makeStyles outside of the component to prevent recreation on every render
const useSecondaryStyles = makeStyles((theme) => {
    return createStyles({
        textFieldNotchedOutline: {
            color: 'black',
            borderColor: 'black !important',
        }
    })
});

/**
 * A reusable form input component based on Material UI's TextField.
 * It supports various input types, styling options, and includes specific handling for password fields.
 *
 * @param {object} params - The parameters object for the FormInput component.
 * @param {any} [params.value] - The current value of the input.
 * @param {boolean} [params.disabled=false] - If true, the input will be disabled.
 * @param {string} [params.title] - The title displayed above the input field.
 * @param {function} [params.onChange] - Callback fired when the value is changed.
 * @param {string} [params.label] - The label content.
 * @param {string} [params.id] - The id of the input element.
 * @param {string} [params.type='text'] - Type of the input element. It can be 'text', 'password', 'number', etc.
 * @param {boolean} [params.required=false] - If true, the label is displayed as required and the input will be required.
 * @param {function} [params.onKeyDown] - Callback fired when a key is pressed down.
 * @param {function} [params.onKeyUp] - Callback fired when a key is released.
 * @param {string} [params.colorScheme='primary'] - Defines the color scheme for the input (e.g., 'primary', 'secondary').
 * @param {boolean} [params.showErrorWhenEmpty=false] - If true, an error state will be shown if the input is empty.
 * @param {boolean} [params.multiline=false] - If true, a textarea element will be rendered instead of an input.
 * @param {number} [params.maxRows=1] - Maximum number of rows to display when multiline is true.
 * @param {number} [params.minRows=1] - Minimum number of rows to display when multiline is true.
 * @param {string} [params.className=''] - Additional CSS class names to apply to the component.
 * @param {boolean} [params.error=false] - If true, the label and input indicator will be in an error state.
 * @param {string} [params.variant] - The variant to use. Can be 'standard', 'filled', or 'outlined'.
 * @param {string} [params.placeholder] - The short hint displayed in the input before the user enters a value.
 * @param {object} [params.InputProps={}] - Props applied to the Input element.
 * @param {object} [params.inputProps={}] - Attributes applied to the native input element.
 * @param {boolean} [params.small=false] - If true, the input will have a smaller size.
 * @param {object} [params.style={}] - Inline style to apply to the root element.
 * @param {boolean} [params.autoFocus=null] - If true, the input element is focused during the first mount.
 * @param {boolean} [params.dontShowErrorMessage] - If true, the error message will not be displayed.
 * @param {function} [params.onBlur] - Callback fired when the input element loses focus.
 * @param {function} [params.onFocus] - Callback fired when the input element gains focus.
 * @param {object} [params.InputLabelProps] - Props applied to the InputLabel element. Defaults to shrink based on value.
 * @param {boolean} [params.hideEyeForPass=false] - If true, the visibility toggle icon for password fields will be hidden.
 * @param {boolean} [params.usePasswordPlaceholder=false] - If true, a generic password placeholder (e.g., "*******") will be used.
 * @returns {JSX.Element} The FormInput component.
 */
export const FormInput = ({
                              value, disabled, title, onChange, label, id, type = 'text', required = false,
                              onKeyDown, onKeyUp, colorScheme = 'primary',
                              showErrorWhenEmpty = false,
                              multiline = false, maxRows  = 1, minRows = 1,
                              className = '', error = false, variant, placeholder, InputProps = {}, inputProps = {}, small = false, style = {}, autoFocus = null, dontShowErrorMessage, onBlur, onFocus,
                              InputLabelProps = {shrink: !_.isNull(value) || !_.isUndefined(value) || !_.isEmpty(String(value))}, hideEyeForPass = false, usePasswordPlaceholder = false,
                              dataTestId
                          }) => {
    const [state, setState] = useState({
        showPassword: false,
        stateType: type
    })
    // Use the styles defined outside the component
    const secondaryClasses = useSecondaryStyles();

    const handleClickShowPassword = () => {
        setState({
            showPassword: !state.showPassword,
            stateType: !state.showPassword ? 'text' : 'password'
        })
    };

    const handleMouseDownPassword = event => {
        event.preventDefault();
    };


    const {showPassword, stateType} = state
    const passwordPlaceholder = "*******";

    if (type === 'password' && !hideEyeForPass) {
        InputProps.endAdornment = <InputAdornment position="end">
            <IconButton
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
            >
                {showPassword ? <Visibility/> : <VisibilityOff/>}
            </IconButton>
        </InputAdornment>
    }
    const enableShrink = (labelProps) => {
        return {
            ...labelProps,
            shrink: true
        }
    }
    if (usePasswordPlaceholder) {
        InputLabelProps = enableShrink(InputLabelProps);
        value = passwordPlaceholder;
    }
    if (colorScheme === 'secondary') {
        InputProps = {
            classes: {
                notchedOutline: secondaryClasses.textFieldNotchedOutline,
            }
        }
    }
    return <ThemeProvider theme={small ? SmallInputTheme : InputTheme}>
        <TextField
            dir="rtl"
            title={title}
            error={value && error || (showErrorWhenEmpty && error)}
            autoFocus={autoFocus}
            required={required}
            type={stateType || type}
            id={id}
            label={label}
            style={style}
            className={`form-input ${className}`}
            placeholder={placeholder}
            value={value}
            variant={variant}
            color={colorScheme}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onBlur={onBlur}
            disabled={disabled}
            onFocus={onFocus}
            inputProps={{ ...inputProps, 'data-testid': dataTestId }}
            InputProps={InputProps}
            InputLabelProps={InputLabelProps}
            helperText={!dontShowErrorMessage ? (error && error !== true ? error : null) : null}
            multiline={multiline}
            maxRows={maxRows}
            minRows={minRows}
        />
    </ThemeProvider>

}
